import React from 'react';
import { Cookies } from 'react-cookie';
import { grey } from '@mui/material/colors';
import { CATEGORIES } from 'constants/common';
import { InfoOutlined } from '@mui/icons-material';
import { useGetCategories } from 'hooks/Categories';
import { ProfileIcon, BusinessIcon } from 'components/SVG';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import CompanyInfoStepperForm from 'components/common/CompanyInfoStepperForm';
import PersonalInfoStepperForm from 'components/common/PersonalInfoStepperForm';
import { useApplicationUserByID } from 'hooks/ApplicationUser';
import WorkSpaceDialogeContentSkeleton from 'sharedComponents/Skeleton/WorkSpaceDialogeContentSkeleton';

type AddWorkspaceDialogContentProps = {
  callBack?: () => void;
  renderWelcomeText?: boolean;
};

function AddWorkspaceDialogContent({
  renderWelcomeText = false,
  callBack,
}: AddWorkspaceDialogContentProps) {
  const [selectedCategoryID, setSelectedCategoryID] = React.useState<
    string | null
  >(null);
  const cookies = new Cookies();
  const userID = cookies.get('userID') as string;
  const { applicationUser } = useApplicationUserByID(userID);
  const { dropdownOptions, categoryIdMap, isLoading } = useGetCategories(null);

  const categories = React.useMemo(() => {
    if (!dropdownOptions.length || !categoryIdMap) {
      return [];
    }
    return dropdownOptions.map((option) => ({
      title: option.label,
      text: option.description,
      id: option.value,
      icon:
        categoryIdMap[option.value].attributes.name === CATEGORIES.PERSONAL ? (
          <ProfileIcon width='24' height='24' />
        ) : (
          <BusinessIcon width='24' height='24' />
        ),
    }));
  }, [categoryIdMap, dropdownOptions]);
  const isPersonalCategory =
    categoryIdMap?.[selectedCategoryID || '']?.attributes.name ===
    CATEGORIES.PERSONAL;
  const isBusinessCategory =
    categoryIdMap?.[selectedCategoryID || '']?.attributes.name ===
    CATEGORIES.BUSINESS;

  const renderCategories = () => {
    if (isLoading) {
      return <WorkSpaceDialogeContentSkeleton />;
    }
    return (
      <Grid container justifyContent='center'>
        {renderWelcomeText && (
          <Grid
            container
            item
            paddingX={2}
            direction={'column'}
            alignItems='flex-start'
            width={'30rem'}
          >
            <Typography
              fontFamily='Mont-semibold'
              justifyContent='flex-start'
              textAlign='start'
              sx={{
                fontSize: '1.5rem',
                marginTop: '1rem',
              }}
            >
              Welcome
              {` ${applicationUser?.attributes?.name}`}
              <span style={{ fontSize: '1.5rem', marginLeft: '0.5rem' }}>
                👋
              </span>
            </Typography>

            <Typography
              fontFamily='Mont-regular'
              justifyContent='center'
              sx={{ fontSize: '1.0rem' }}
            >
              Do you want to use SparkUp for Personal or Business purposes?
              <Tooltip title={'You can add more workspaces in future'}>
                <InfoOutlined
                  viewBox='0 0 24 24'
                  sx={{
                    cursor: 'pointer',
                    color: grey[600],
                    marginBottom: '-0.2rem',
                    marginLeft: '0.2rem',
                  }}
                  fontSize='small'
                />
              </Tooltip>
            </Typography>
          </Grid>
        )}
        <Grid
          container
          gap={2}
          item
          xs={12}
          justifyContent='center'
          paddingTop={2}
        >
          {categories.map((category, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '10px',
                minWidth: '100px',
                maxWidth: '220px',
                aspectRatio: 1,

                backgroundColor: 'var(--color-white)',
                outline: (theme) => `1px solid ${theme.palette.borderColor}`,
                boxShadow: 'none',
                transition: 'none',
                '&:hover': {
                  backgroundColor: 'var(--color-white)',
                  boxShadow: 'none',
                  outline: '1px solid var( --color-primary)',
                  transition: 'none',
                  cursor: 'pointer',
                },
              }}
              onClick={() => setSelectedCategoryID(category.id as string)}
            >
              <Box
                sx={{
                  padding: '16px',
                  backgroundColor: 'var(--color-white)',
                  '&:hover': {
                    boxShadow: 'none',
                    backgroundColor: 'inherit',
                    transition: 'none',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      width: '50px',
                      height: '50px',
                      borderRadius: 1,
                      bgcolor: (theme) => theme.palette.primary.light,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {category.icon}
                  </Box>
                  <Typography
                    sx={{
                      textWrap: 'pretty',
                    }}
                    fontFamily='Mont-semibold'
                  >
                    {category.title}
                  </Typography>
                  <Typography
                    variant='subtitle2'
                    sx={{
                      textWrap: 'wrap',
                      textAlign: 'center',
                    }}
                  >
                    {category.text}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Grid>
      </Grid>
    );
  };

  const handleSave = () => {
    callBack && callBack();
  };

  return (
    <Grid item container justifyContent='center' gap={3}>
      {selectedCategoryID === null && <>{renderCategories()}</>}
      {isPersonalCategory && (
        <PersonalInfoStepperForm
          onSave={handleSave}
          categoryID={selectedCategoryID}
          onResetCategory={() => setSelectedCategoryID(null)}
        />
      )}
      {isBusinessCategory && (
        <CompanyInfoStepperForm
          onSave={handleSave}
          categoryID={selectedCategoryID}
          onResetCategory={() => setSelectedCategoryID(null)}
        />
      )}
    </Grid>
  );
}

export default AddWorkspaceDialogContent;
